import React from 'react';
import { useAppSelector } from 'src/store';
import { getAdminRoutesAvailability, getAppRoutesAvailability } from 'src/routes/state/routesSelector';
import { useLocation } from 'react-router-dom';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import useAuthService from 'src/shared/hooks/useAuthService';
import { Settings } from 'lucide-react';
import NestedSidebarMenuItem, { NestedSidebarMenuLeafItem, NestedSidebarMenuSubItem } from './NestedSidebarMenuItem';

const availableOnly = (items: (NestedSidebarMenuLeafItem | false)[]) =>
    items.filter((item): item is NestedSidebarMenuLeafItem => Boolean(item));

const AdminMenu = () => {
    const appRoutesAvailability = useAppSelector(getAppRoutesAvailability);
    const adminRoutesAvailability = useAppSelector(getAdminRoutesAvailability);
    const navigate = useAppNavigation();
    const { pathname } = useLocation();
    const { isCurrentUserSuperAdmin } = useAuthService();

    if (!appRoutesAvailability.admin) {
        return null;
    }

    const isNatureRisk = pathname.startsWith('/admin/management/nature-risk');
    const isOneOrganisation = pathname.startsWith('/admin/management') && !isNatureRisk;

    const organisationItems = availableOnly([
        adminRoutesAvailability.gnpManagement && {
            title: 'Nature Performance',
            testId: 'admin-org-management-menu-button',
            to: navigate.paths.organisationManagement(),
            isActive: isOneOrganisation,
        },
        adminRoutesAvailability.riskPortfolio && {
            title: 'Nature Risk',
            testId: 'admin-risk-portfolio-menu-button',
            to: navigate.paths.riskPortfolioAdmin(),
            isActive: isNatureRisk,
        },
        adminRoutesAvailability.userManagement && {
            title: 'User Management',
            testId: 'admin-menu-button',
            to: navigate.paths.userManagement(),
            isActive: pathname.startsWith('/admin/user-management'),
        },
    ]);

    // Pages about the whole system, not the current organisation. Each page is already super-admin
    // only, but the group checks again: a wrong availability flag must not show it to anyone else.
    const systemItems = !isCurrentUserSuperAdmin
        ? []
        : availableOnly([
              adminRoutesAvailability.engineApiTokens && {
                  title: 'Engine API Tokens',
                  testId: 'admin-engine-api-tokens-menu-button',
                  to: navigate.paths.engineApiTokens(),
                  isActive: pathname.startsWith('/admin/engine-api-tokens'),
              },
              adminRoutesAvailability.geospatialCatalogue && {
                  title: 'Geospatial Catalogue',
                  testId: 'admin-stac-catalogue-menu-button',
                  to: navigate.paths.geospatialCatalogue(),
                  isActive: pathname.startsWith('/admin/catalogue'),
              },
              adminRoutesAvailability.estateManagement && {
                  title: 'Estate Management',
                  testId: 'admin-estate-management-menu-button',
                  to: navigate.paths.estateManagement(),
                  isActive: pathname.startsWith('/admin/estate-management'),
              },
          ]);

    const subItems: NestedSidebarMenuSubItem[] = [
        ...organisationItems,
        ...(systemItems.length > 0
            ? [{ title: 'System Admin', testId: 'admin-system-admin-menu-group-button', subItems: systemItems }]
            : []),
    ];

    if (subItems.length === 0) {
        return null;
    }

    const isAdminActive = [...organisationItems, ...systemItems].some(item => item.isActive);

    return (
        <NestedSidebarMenuItem
            label='Admin'
            testId='admin-menu-group-button'
            icon={<Settings />}
            isActive={isAdminActive}
            defaultOpen={isAdminActive}
            subItems={subItems}
        />
    );
};

export default AdminMenu;
